import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ChartLineUp, ArrowRight, CheckCircle, Buildings, ShieldCheck,
} from "@phosphor-icons/react";

const INDUSTRIES = [
  "Gastronomie / Restaurant",
  "Catering / Großküche",
  "Lebensmittelhersteller",
  "Fleischerei / Metzgerei",
  "Bäckerei / Konditorei",
  "Manufaktur / Hofladen",
  "Lebensmittelhandel",
  "Sonstiges",
];
const SIZES = ["1-5", "6-20", "21-50", "51-200", "200+"];

const BENEFITS = [
  "7 Tage kostenlos testen",
  "Voller Funktionsumfang",
  "Keine Kreditkarte nötig",
  "DSGVO-konform",
  "Daten in Deutschland",
];

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    full_name: "",
    email: "",
    password: "",
    phone: "",
    industry: "",
    employee_count: "",
    accepted_terms: false,
    newsletter_opt_in: false,
    website: "", // honeypot
  });
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.company_name.trim() || !form.full_name.trim() || !form.email.trim()) {
      toast.error("Bitte Firmenname, Name und E-Mail ausfüllen"); return;
    }
    if (form.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen lang sein"); return;
    }
    if (!form.accepted_terms) {
      toast.error("Bitte akzeptiere AGB und Datenschutzerklärung"); return;
    }
    setBusy(true);
    try {
      await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        company_name: form.company_name.trim(),
      });
      toast.success("Willkommen bei NutriCalc! Deine 7-tägige Testphase startet jetzt.");
      nav("/app");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Registrierung fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Registrierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-white" data-testid="register-page">
      {/* ---------- Left: Form ---------- */}
      <div className="px-5 py-8 sm:px-12 sm:py-12 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-logo-link">
          <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center">
            <ChartLineUp size={18} weight="bold" color="white" />
          </div>
          <span className="font-heading font-bold text-base tracking-tight">NutriCalc</span>
        </Link>

        <div className="max-w-md w-full mx-auto flex-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] uppercase tracking-widest rounded-sm mb-4">
            <ShieldCheck size={11} weight="fill" /> 7 Tage gratis · keine Kreditkarte
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            Kostenlos starten.
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Erstelle dein Konto in unter einer Minute. Direkt nach der Registrierung kannst du loslegen.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="register-form">
            {/* Honeypot — versteckt für echte Nutzer, sichtbar für Bots */}
            <div className="hidden" aria-hidden="true">
              <Label>Website</Label>
              <Input tabIndex={-1} autoComplete="off"
                value={form.website} onChange={(e) => set("website", e.target.value)} />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Firmenname *</Label>
              <Input value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                className="mt-1 rounded-sm" placeholder="z. B. Bäckerei Müller GmbH"
                data-testid="reg-company-name" required />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Voller Name *</Label>
                <Input value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  className="mt-1 rounded-sm" placeholder="Max Mustermann"
                  data-testid="reg-full-name" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Telefon</Label>
                <Input value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1 rounded-sm font-mono-num" placeholder="+49 ..."
                  data-testid="reg-phone" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Geschäftliche E-Mail *</Label>
              <Input type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1 rounded-sm" placeholder="dein.name@firma.de"
                data-testid="reg-email" required />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Passwort * (min. 8 Zeichen)</Label>
              <Input type="password" value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className="mt-1 rounded-sm font-mono-num"
                data-testid="reg-password" required minLength={8} />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Branche</Label>
                <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="reg-industry">
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((i) => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-600">Mitarbeiter</Label>
                <Select value={form.employee_count} onValueChange={(v) => set("employee_count", v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="reg-employees">
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* DSGVO Pflicht-Checkboxen */}
            <div className="space-y-2.5 pt-2 border-t border-zinc-100">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox checked={form.accepted_terms}
                  onCheckedChange={(c) => set("accepted_terms", !!c)}
                  className="mt-0.5"
                  data-testid="reg-accept-terms" />
                <span className="text-xs text-zinc-700 leading-relaxed">
                  Ich akzeptiere die <Link to="/agb" target="_blank" className="text-[#002FA7] underline hover:no-underline">AGB</Link> und
                  habe die <Link to="/datenschutz" target="_blank" className="text-[#002FA7] underline hover:no-underline">Datenschutzerklärung</Link> gelesen. *
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox checked={form.newsletter_opt_in}
                  onCheckedChange={(c) => set("newsletter_opt_in", !!c)}
                  className="mt-0.5"
                  data-testid="reg-newsletter" />
                <span className="text-xs text-zinc-700 leading-relaxed">
                  Ja, ich möchte gelegentlich Produkt-Updates und Tipps per E-Mail erhalten. Abmeldung jederzeit möglich.
                </span>
              </label>
            </div>

            <Button type="submit" disabled={busy}
              className="w-full bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-11 text-sm font-medium mt-4"
              data-testid="reg-submit">
              {busy ? "Konto wird angelegt…" : (
                <span className="inline-flex items-center">
                  Konto erstellen <ArrowRight size={16} className="ml-2" weight="bold" />
                </span>
              )}
            </Button>

            <p className="text-xs text-zinc-500 text-center mt-2">
              Bereits ein Konto? {" "}
              <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="reg-to-login">
                Hier anmelden
              </Link>
            </p>
          </form>
        </div>

        <div className="text-[11px] text-zinc-400 mt-8 text-center">
          NutriCalc by Vahit Sarpkaya · {" "}
          <Link to="/impressum" className="hover:text-zinc-600">Impressum</Link> · {" "}
          <Link to="/datenschutz" className="hover:text-zinc-600">Datenschutz</Link> · {" "}
          <Link to="/agb" className="hover:text-zinc-600">AGB</Link>
        </div>
      </div>

      {/* ---------- Right: Benefits ---------- */}
      <div className="hidden lg:block relative bg-gradient-to-br from-[#002FA7] via-[#001f7a] to-[#001550] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(34,197,94,0.4) 0%, transparent 50%)",
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-12 lg:p-16">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-3">Was du bekommst</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold leading-tight">
              Deine eigene Nährwert-Software.
              <span className="text-emerald-300"> Sofort. Einfach.</span>
            </h2>
            <ul className="mt-8 space-y-3">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-center gap-3 text-base">
                  <CheckCircle size={20} weight="fill" className="text-emerald-300" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-emerald-500 flex items-center justify-center font-heading font-extrabold text-xl">A</div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Nutri-Score</div>
                  <div className="font-heading text-lg font-bold leading-none">Live-Berechnung</div>
                </div>
              </div>
              <p className="text-xs opacity-80 leading-relaxed">
                Jedes Rezept bekommt sofort eine A–E-Klassifizierung. Verbessere die Bewertung
                durch Anpassung der Zutaten — in Echtzeit.
              </p>
            </div>

            <div className="flex items-start gap-3 text-xs opacity-80">
              <Buildings size={16} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <p>
                Nach 7 Tagen kannst du auf den Team-Plan upgraden (149 € / Monat für 5 Nutzer) —
                ohne Datenverlust. Keine automatische Verlängerung der Testphase.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
